import React from 'react';
import { IonInput, IonItem } from '@ionic/react';
import { useSmartCase } from '@/modules/footer';
import ClearButton from './ClearButton';
import { MAX_LOCATION_LENGTH } from './constants';
import styles from './LocationRow.module.css';

interface LocationRowProps {
  location: string;
  /** Called with each edit to the typed place. */
  onChange: (value: string) => void;
  /** Called by the row's ⓧ, shown while a place is typed. */
  onClear: () => void;
  /** The row's label, in sentence case, such as "Location". */
  label: string;
  lines?: 'full' | 'inset' | 'none';
  /** Prefixes the input's and ⓧ's test ids: `<prefix>-input`, `<prefix>-clear`. */
  testIdPrefix?: string;
}

/**
 * The topic's location, as a row in a section card: its label above an input
 * as wide as the row, so a city and state fit.
 */
const LocationRow: React.FC<LocationRowProps> = ({
  location,
  onChange,
  onClear,
  label,
  lines = 'none',
  testIdPrefix = 'location',
}) => {
  const smartCase = useSmartCase();
  return (
    <IonItem lines={lines}>
      <IonInput
        className={styles.locationInput}
        label={smartCase(label)}
        labelPlacement="stacked"
        value={location}
        onIonInput={e => onChange((e.detail.value as string) || '')}
        placeholder={`Add ${label.toLowerCase()}`}
        maxlength={MAX_LOCATION_LENGTH}
        autocapitalize="words"
        autocomplete="off"
        autocorrect="off"
        spellcheck={false}
        data-testid={`${testIdPrefix}-input`}
      >
        {/* The ⓧ sits in the input's own end slot, so it lines up with the
            typed text rather than centering on the label and input together.
            The slot holds a span that always renders: Ionic moves slotted
            children into its own markup, so React could not remove the ⓧ
            itself from there when the place is cleared. */}
        <span slot="end" className={styles.clearSlot}>
          {location !== '' && (
            <ClearButton
              label={`Clear ${label.toLowerCase()}`}
              testId={`${testIdPrefix}-clear`}
              onClick={onClear}
            />
          )}
        </span>
      </IonInput>
    </IonItem>
  );
};

export default LocationRow;
